import { ReactNode } from 'react';
import styles from './sidebar-menu.module.scss';

export interface SidebarMenuItem {
  id: string;
  icon: ReactNode;
  label: string;
  href?: string;
  isActive?: boolean;
  onClick?: () => void;
  title?: string;
  testId: string;
}

export interface SidebarMenuProps {
  items: SidebarMenuItem[];
  renderLink?: (
    item: SidebarMenuItem,
    children: ReactNode,
    className: string,
    ariaCurrent?: 'page',
  ) => ReactNode;
  ariaLabel?: string;
}

function MenuItem({
  item,
  renderLink,
}: {
  item: SidebarMenuItem;
  renderLink?: SidebarMenuProps['renderLink'];
}) {
  const linkClasses = `${styles.link} ${item.isActive ? styles['link-active'] : ''}`;
  const ariaCurrent = item.isActive ? 'page' : undefined;

  const content = (
    <span className={styles['link-content']}>
      <span aria-hidden="true">{item.icon}</span>
      <span className={styles['link-label']}>{item.label}</span>
    </span>
  );

  if (renderLink) {
    return <li>{renderLink(item, content, linkClasses, ariaCurrent)}</li>;
  }

  if (item.href) {
    return (
      <li>
        <a
          href={item.href}
          className={linkClasses}
          aria-current={ariaCurrent}
          data-testid={item.testId}
        >
          {content}
        </a>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={item.onClick}
        className={linkClasses}
        aria-current={ariaCurrent}
        data-testid={item.testId}
      >
        {content}
      </button>
    </li>
  );
}

export function SidebarMenu({
  items,
  renderLink,
  ariaLabel = 'Hlavní navigace',
}: SidebarMenuProps) {
  return (
    <nav className={styles.sidebar} aria-label={ariaLabel} data-theme="dark">
      <ul className={styles.items}>
        {items.map((item) => (
          <MenuItem key={item.id} item={item} renderLink={renderLink} />
        ))}
      </ul>
    </nav>
  );
}
